import React, { useState, useEffect, useCallback } from 'react';
import { Save, X, User, Phone, MapPin, Plus, Trash2, Building, DollarSign, FileText, AlertCircle } from 'lucide-react';
import Input from '../../components/forms/Input';
import AddressInput from '../../components/forms/AddressInput';
import Button from '../../components/ui/Button';
import MapViewer from '../../components/maps/MapViewer';
import apiService from '../../services/apiService';
import './CustomerForm.css';

const CustomerForm = ({ cliente, onSubmit, onCancel, isLoading = false, isEdit = false }) => {
    const [activeTab, setActiveTab] = useState('basico');
    const [formData, setFormData] = useState({
        nombre: '',
        nombre_comercial: '',
        precio_venta: 'precio_lista', // Default precio_lista
        activo: true,
        tiene_credito: false,
        monto_credito_maximo: 0,
        dias_credito: 15,
        telefonos: [{ telefono: '', tipo: 'principal', whatsapp: false }],
        direcciones: [{ direccion: '', es_principal: true, latitud: null, longitud: null }],
        datos_fiscales: []
    });

    // Catálogos SAT para datos fiscales
    const [catalogos, setCatalogos] = useState({
        regimenesFiscales: [],
        usosCFDI: []
    });
    const [catalogosLoaded, setCatalogosLoaded] = useState(false);

    const [errors, setErrors] = useState({});
    const [selectedDireccionIndex, setSelectedDireccionIndex] = useState(0); // Activar primera dirección por defecto

    const tiposTelefono = [
        { value: 'principal', label: 'Principal' },
        { value: 'casa', label: 'Casa' },
        { value: 'trabajo', label: 'Trabajo' },
        { value: 'otro', label: 'Otro' }
    ];

    // Cargar catálogos SAT al montar el componente
    const cargarCatalogos = useCallback(async () => {
        try {
            const [regimenesRes, usosRes] = await Promise.all([
                apiService.getCatalogoRegimenFiscal(),
                apiService.getCatalogoUsoCFDI()
            ]);

            setCatalogos({
                regimenesFiscales: regimenesRes.success ? regimenesRes.data : [],
                usosCFDI: usosRes.success ? usosRes.data : []
            });
            setCatalogosLoaded(true);
        } catch (error) {
            console.error('Error cargando catálogos SAT:', error);
        }
    }, []);

    useEffect(() => {
        cargarCatalogos();
    }, [cargarCatalogos]);

    useEffect(() => {
        if (isEdit && cliente) {
            setFormData({
                nombre: cliente.nombre || '',
                nombre_comercial: cliente.nombre_comercial || '',
                precio_venta: cliente.precio_venta || 'precio_lista',
                activo: cliente.activo !== undefined ? cliente.activo : true,
                tiene_credito: cliente.tiene_credito ? true : false,
                monto_credito_maximo: cliente.monto_credito_maximo || 0,
                dias_credito: cliente.dias_credito || 15,
                telefonos: cliente.telefonos && cliente.telefonos.length > 0
                    ? cliente.telefonos.map(tel => ({ ...tel, whatsapp: tel.whatsapp || false }))
                    : [{ telefono: '', tipo: 'principal', whatsapp: false }],
                direcciones: cliente.direcciones && cliente.direcciones.length > 0
                    ? cliente.direcciones
                    : [{ direccion: '', es_principal: true }],
                datos_fiscales: cliente.datos_fiscales && cliente.datos_fiscales.length > 0
                    ? cliente.datos_fiscales
                    : []
            });
        }
    }, [isEdit, cliente]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: null
            }));
        }
    };

    const handleTelefonoChange = (index, field, value) => {
        const newTelefonos = [...formData.telefonos];
        newTelefonos[index] = {
            ...newTelefonos[index],
            [field]: field === 'telefono' ? value.replace(/\D/g, '') : value
        };

        setFormData(prev => ({
            ...prev,
            telefonos: newTelefonos
        }));

        if (errors[`telefono_${index}`]) {
            setErrors(prev => ({
                ...prev,
                [`telefono_${index}`]: null
            }));
        }
    };

    const handleDireccionChange = (index, field, value) => {
        const newDirecciones = [...formData.direcciones];

        if (field === 'es_principal' && value) {
            // Si se marca como principal, desmarcar todas las demás
            newDirecciones.forEach((dir, i) => {
                dir.es_principal = i === index;
            });
        } else {
            newDirecciones[index] = {
                ...newDirecciones[index],
                [field]: value
            };
        }

        setFormData(prev => ({
            ...prev,
            direcciones: newDirecciones
        }));

        if (errors[`direccion_${index}`]) {
            setErrors(prev => ({
                ...prev,
                [`direccion_${index}`]: null
            }));
        }
    };

    const agregarTelefono = () => {
        if (formData.telefonos.length < 5) { // Limitar a 5 teléfonos
            setFormData(prev => ({
                ...prev,
                telefonos: [...prev.telefonos, { telefono: '', tipo: 'otro', whatsapp: false }]
            }));
        }
    };

    const eliminarTelefono = (index) => {
        if (formData.telefonos.length > 1) {
            if (window.confirm('¿Estás seguro de que deseas eliminar este teléfono?')) {
                setFormData(prev => ({
                    ...prev,
                    telefonos: prev.telefonos.filter((_, i) => i !== index)
                }));
            }
        }
    };

    const agregarDireccion = () => {
        if (formData.direcciones.length < 3) { // Limitar a 3 direcciones
            const newIndex = formData.direcciones.length;
            setFormData(prev => ({
                ...prev,
                direcciones: [...prev.direcciones, { direccion: '', es_principal: false, latitud: null, longitud: null }]
            }));
            // Seleccionar automáticamente la nueva dirección
            setSelectedDireccionIndex(newIndex);
        }
    };

    const handleDireccionFocus = (index) => {
        setSelectedDireccionIndex(index);
    };

    const handleLocationSelect = (index, locationData) => {
        const newDirecciones = [...formData.direcciones];
        newDirecciones[index] = {
            ...newDirecciones[index],
            latitud: locationData.lat,
            longitud: locationData.lng,
            // Si viene la dirección de la geocodificación inversa, actualizarla
            direccion: locationData.address || newDirecciones[index].direccion
        };

        setFormData(prev => ({
            ...prev,
            direcciones: newDirecciones
        }));
    };

    const handleAddressAutocomplete = (index, placeData) => {
        const newDirecciones = [...formData.direcciones];
        newDirecciones[index] = {
            ...newDirecciones[index],
            direccion: placeData.address,
            latitud: placeData.lat,
            longitud: placeData.lng
        };

        setFormData(prev => ({
            ...prev,
            direcciones: newDirecciones
        }));

        // Limpiar error si existe
        if (errors[`direccion_${index}`]) {
            setErrors(prev => ({
                ...prev,
                [`direccion_${index}`]: null
            }));
        }
    };

    const eliminarDireccion = (index) => {
        if (formData.direcciones.length > 1) {
            if (window.confirm('¿Estás seguro de que deseas eliminar esta dirección?')) {
                setFormData(prev => ({
                    ...prev,
                    direcciones: prev.direcciones.filter((_, i) => i !== index)
                }));
            }
        }
    };

    // ===== FUNCIONES PARA DATOS FISCALES =====

    const agregarDatoFiscal = () => {
        if (formData.datos_fiscales.length < 5) { // Máximo 5 razones sociales
            const esPrimera = formData.datos_fiscales.length === 0;
            setFormData(prev => ({
                ...prev,
                datos_fiscales: [...prev.datos_fiscales, {
                    rfc: '',
                    razon_social: '',
                    regimen_fiscal: '',
                    uso_cfdi: '',
                    codigo_postal_fiscal: '',
                    email_facturacion: '',
                    es_principal: esPrimera
                }]
            }));
        }
    };

    const handleDatoFiscalChange = (index, field, value) => {
        const newDatosFiscales = [...formData.datos_fiscales];

        if (field === 'es_principal' && value) {
            // Si se marca como principal, desmarcar los demás
            newDatosFiscales.forEach((df, i) => {
                df.es_principal = i === index;
            });
        } else if (field === 'rfc') {
            // Convertir a mayúsculas y limpiar
            newDatosFiscales[index] = {
                ...newDatosFiscales[index],
                [field]: value.toUpperCase().replace(/[^A-Z0-9&Ñ]/g, '')
            };
        } else if (field === 'codigo_postal_fiscal') {
            // Solo números
            newDatosFiscales[index] = {
                ...newDatosFiscales[index],
                [field]: value.replace(/\D/g, '').slice(0, 5)
            };
        } else {
            newDatosFiscales[index] = {
                ...newDatosFiscales[index],
                [field]: value
            };
        }

        setFormData(prev => ({
            ...prev,
            datos_fiscales: newDatosFiscales
        }));

        // Limpiar error si existe
        if (errors[`dato_fiscal_${index}_${field}`]) {
            setErrors(prev => ({
                ...prev,
                [`dato_fiscal_${index}_${field}`]: null
            }));
        }
    };

    const eliminarDatoFiscal = (index) => {
        if (window.confirm('¿Estás seguro de que deseas eliminar estos datos fiscales?')) {
            const newDatosFiscales = formData.datos_fiscales.filter((_, i) => i !== index);

            // Si el eliminado era principal y quedan datos, hacer principal al primero
            if (formData.datos_fiscales[index].es_principal && newDatosFiscales.length > 0) {
                newDatosFiscales[0].es_principal = true;
            }

            setFormData(prev => ({
                ...prev,
                datos_fiscales: newDatosFiscales
            }));
        }
    };

    // Obtener tipo de persona por RFC
    const getTipoPersonaByRFC = (rfc) => {
        const rfcLength = rfc?.trim().length || 0;
        if (rfcLength === 12) return 'moral';
        if (rfcLength === 13) return 'fisica';
        return null;
    };

    // Filtrar regímenes según tipo de persona
    const getRegimenesFiltrados = (rfc) => {
        const tipoPersona = getTipoPersonaByRFC(rfc);
        if (!tipoPersona || !catalogos.regimenesFiscales.length) return catalogos.regimenesFiscales;

        return catalogos.regimenesFiscales.filter(r =>
            tipoPersona === 'fisica' ? r.persona_fisica : r.persona_moral
        );
    };

    // Filtrar usos CFDI según tipo de persona
    const getUsosCFDIFiltrados = (rfc) => {
        const tipoPersona = getTipoPersonaByRFC(rfc);
        if (!tipoPersona || !catalogos.usosCFDI.length) return catalogos.usosCFDI;

        return catalogos.usosCFDI.filter(u =>
            tipoPersona === 'fisica' ? u.persona_fisica : u.persona_moral
        );
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.nombre.trim()) {
            newErrors.nombre = 'El nombre es requerido';
        }

        // Validar teléfonos
        let telefonosValidos = 0;
        formData.telefonos.forEach((tel, index) => {
            const telefono = tel.telefono.trim();
            if (telefono) {
                if (!/^\d{10}$/.test(telefono)) {
                    newErrors[`telefono_${index}`] = 'Debe tener 10 dígitos';
                } else {
                    telefonosValidos++;
                }
            }
        });

        if (telefonosValidos === 0) {
            newErrors.telefonos_general = 'Al menos un teléfono requerido';
        }

        // Validar datos fiscales (si hay alguno)
        formData.datos_fiscales.forEach((df, index) => {
            // RFC
            if (df.rfc.trim()) {
                const rfcLength = df.rfc.trim().length;
                if (rfcLength !== 12 && rfcLength !== 13) {
                    newErrors[`dato_fiscal_${index}_rfc`] = 'RFC debe tener 12 o 13 caracteres';
                }
            } else if (df.razon_social.trim()) {
                // Si hay razón social pero no RFC
                newErrors[`dato_fiscal_${index}_rfc`] = 'RFC es requerido';
            }

            // Razón Social
            if (df.rfc.trim() && !df.razon_social.trim()) {
                newErrors[`dato_fiscal_${index}_razon_social`] = 'Razón social es requerida';
            }

            // Régimen Fiscal
            if (df.rfc.trim() && !df.regimen_fiscal) {
                newErrors[`dato_fiscal_${index}_regimen_fiscal`] = 'Régimen fiscal es requerido';
            }

            // Código Postal
            if (df.rfc.trim() && df.codigo_postal_fiscal.trim()) {
                if (!/^\d{5}$/.test(df.codigo_postal_fiscal.trim())) {
                    newErrors[`dato_fiscal_${index}_codigo_postal_fiscal`] = 'Debe tener 5 dígitos';
                }
            } else if (df.rfc.trim() && !df.codigo_postal_fiscal.trim()) {
                newErrors[`dato_fiscal_${index}_codigo_postal_fiscal`] = 'C.P. fiscal es requerido';
            }

            // Email (opcional pero validar formato si existe)
            if (df.email_facturacion && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(df.email_facturacion)) {
                newErrors[`dato_fiscal_${index}_email_facturacion`] = 'Email inválido';
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        // Filtrar datos fiscales completos (que tengan RFC)
        const datosFiscalesValidos = formData.datos_fiscales.filter(df => df.rfc.trim());

        const submitData = {
            nombre: formData.nombre.trim(),
            nombre_comercial: formData.nombre_comercial.trim(),
            precio_venta: formData.precio_venta,
            activo: formData.activo,
            tiene_credito: formData.tiene_credito ? 1 : 0,
            monto_credito_maximo: formData.tiene_credito ? parseFloat(formData.monto_credito_maximo) : 0,
            dias_credito: formData.tiene_credito ? parseInt(formData.dias_credito) : 0,
            telefonos: formData.telefonos.filter(tel => tel.telefono.trim()),
            direcciones: formData.direcciones.filter(dir => dir.direccion.trim()),
            datos_fiscales: datosFiscalesValidos
        };

        onSubmit(submitData);
    };

    return (
        <div className="customer-form-with-map">
            <form onSubmit={handleSubmit} className="customer-form-compact">

                {/* Tabs */}
                <div className="customer-tabs">
                    <button
                        type="button"
                        className={`customer-tab ${activeTab === 'basico' ? 'active' : ''}`}
                        onClick={() => setActiveTab('basico')}
                    >
                        <User size={16} />
                        Información Básica
                    </button>
                    <button
                        type="button"
                        className={`customer-tab ${activeTab === 'configuracion' ? 'active' : ''}`}
                        onClick={() => setActiveTab('configuracion')}
                    >
                        <DollarSign size={16} />
                        Configuración
                    </button>
                    <button
                        type="button"
                        className={`customer-tab ${activeTab === 'direcciones' ? 'active' : ''}`}
                        onClick={() => setActiveTab('direcciones')}
                    >
                        <MapPin size={16} />
                        Direcciones
                    </button>
                    <button
                        type="button"
                        className={`customer-tab ${activeTab === 'fiscal' ? 'active' : ''}`}
                        onClick={() => setActiveTab('fiscal')}
                    >
                        <FileText size={16} />
                        Datos Fiscales
                        {formData.datos_fiscales.length > 0 && (
                            <span className="tab-badge">{formData.datos_fiscales.length}</span>
                        )}
                    </button>
                </div>

                <div className="form-content-compact">

                    {/* Tab: Información Básica */}
                    {activeTab === 'basico' && (
                        <>
                            <div className="form-section-compact">
                                <div className="form-grid-compact">
                                    <Input
                                        label="Nombre *"
                                        value={formData.nombre}
                                        onChange={(e) => handleInputChange('nombre', e.target.value)}
                                        error={errors.nombre}
                                        placeholder="Nombre del cliente"
                                        icon={<User size={16} />}
                                        maxLength={100}
                                    />
                                    <Input
                                        label="Nombre Comercial"
                                        value={formData.nombre_comercial}
                                        onChange={(e) => handleInputChange('nombre_comercial', e.target.value)}
                                        placeholder="Negocio (opcional)"
                                        icon={<Building size={16} />}
                                        maxLength={100}
                                    />
                                </div>

                                {/* Toggle de estado (solo en edición) */}
                                {isEdit && (
                                    <div className="toggle-container">
                                        <span className="toggle-label">Estado del cliente</span>
                                        <label className="toggle-switch">
                                            <input
                                                type="checkbox"
                                                checked={formData.activo}
                                                onChange={(e) => handleInputChange('activo', e.target.checked)}
                                            />
                                            <span className="toggle-slider"></span>
                                        </label>
                                        <span style={{
                                            fontSize: '13px',
                                            color: formData.activo ? '#059669' : '#6b7280',
                                            fontWeight: 500,
                                            minWidth: '60px'
                                        }}>
                                            {formData.activo ? 'Activo' : 'Inactivo'}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* Teléfonos */}
                            <div className="form-section-compact">
                                <div className="section-header-compact">
                                    <label className="section-label-compact">
                                        <Phone size={14} />
                                        Teléfonos *
                                    </label>
                                    {formData.telefonos.length < 5 && (
                                        <button
                                            type="button"
                                            className="add-btn-compact"
                                            onClick={agregarTelefono}
                                            disabled={isLoading}
                                            title="Agregar teléfono"
                                        >
                                            <Plus size={14} />
                                        </button>
                                    )}
                                </div>

                                {errors.telefonos_general && (
                                    <div className="error-inline">{errors.telefonos_general}</div>
                                )}

                                <div className="items-grid-compact">
                                    {formData.telefonos.map((telefono, index) => (
                                        <div key={index} className="item-compact-phone">
                                            <input
                                                type="tel"
                                                value={telefono.telefono}
                                                onChange={(e) => handleTelefonoChange(index, 'telefono', e.target.value)}
                                                onFocus={(e) => e.target.select()}
                                                placeholder="10 dígitos"
                                                maxLength={10}
                                                className={`input-compact ${errors[`telefono_${index}`] ? 'error' : ''}`}
                                            />
                                            <select
                                                value={telefono.tipo}
                                                onChange={(e) => handleTelefonoChange(index, 'tipo', e.target.value)}
                                                className="select-compact"
                                                disabled={isLoading}
                                            >
                                                {tiposTelefono.map(tipo => (
                                                    <option key={tipo.value} value={tipo.value}>
                                                        {tipo.label}
                                                    </option>
                                                ))}
                                            </select>
                                            <label className="checkbox-compact-phone">
                                                <input
                                                    type="checkbox"
                                                    checked={telefono.whatsapp || false}
                                                    onChange={(e) => handleTelefonoChange(index, 'whatsapp', e.target.checked)}
                                                    disabled={isLoading}
                                                />
                                                <span>WhatsApp</span>
                                            </label>
                                            {formData.telefonos.length > 1 && (
                                                <button
                                                    type="button"
                                                    className="delete-btn-compact"
                                                    onClick={() => eliminarTelefono(index)}
                                                    title="Eliminar"
                                                >
                                                    <Trash2 size={12} />
                                                </button>
                                            )}
                                            {errors[`telefono_${index}`] && (
                                                <div className="error-text-compact">{errors[`telefono_${index}`]}</div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}

                    {/* Tab: Configuración (Precio + Crédito) */}
                    {activeTab === 'configuracion' && (
                        <>
                            {/* Precio de venta */}
                            <div className="form-section-compact">
                                <div className="form-grid-compact">
                                    <div className="form-field">
                                        <label className="form-label">
                                            <DollarSign size={16} />
                                            Precio de Venta *
                                        </label>
                                        <select
                                            value={formData.precio_venta}
                                            onChange={(e) => handleInputChange('precio_venta', e.target.value)}
                                            className="select-compact"
                                            disabled={isLoading}
                                            style={{
                                                padding: '8px 12px',
                                                border: '1px solid #d1d5db',
                                                borderRadius: '6px',
                                                fontSize: '14px',
                                                width: '100%'
                                            }}
                                        >
                                            <option value="precio_lista">Precio Lista</option>
                                            <option value="precio1">Precio 1</option>
                                            <option value="precio2">Precio 2</option>
                                            <option value="precio3">Precio 3</option>
                                            <option value="precio4">Precio 4</option>
                                            <option value="precio5">Precio 5</option>
                                            <option value="precio6">Precio 6</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Política de Crédito */}
                            <div className="form-section-compact">
                                <div className="section-header-compact">
                                    <label className="section-label-compact">

                                        Política de Crédito
                                    </label>
                                    <label className="checkbox-compact-inline">
                                        <input
                                            type="checkbox"
                                            checked={formData.tiene_credito}
                                            onChange={(e) => handleInputChange('tiene_credito', e.target.checked)}
                                            disabled={isLoading}
                                        />
                                        <span>Habilitar crédito</span>
                                    </label>
                                </div>

                                {formData.tiene_credito && (
                                    <div className="credit-fields">
                                        <div className="form-grid-compact">
                                            <div className="form-field">
                                                <label className="form-label">
                                                    <DollarSign size={14} />
                                                    Monto Máximo de Crédito *
                                                </label>
                                                <input
                                                    type="number"
                                                    value={formData.monto_credito_maximo}
                                                    onChange={(e) => handleInputChange('monto_credito_maximo', e.target.value)}
                                                    onFocus={(e) => e.target.select()}
                                                    className="input-compact"
                                                    placeholder="0.00"
                                                    min="0"
                                                    step="0.01"
                                                    disabled={isLoading}
                                                    style={{
                                                        padding: '8px 12px',
                                                        border: '1px solid #d1d5db',
                                                        borderRadius: '6px',
                                                        fontSize: '14px',
                                                        width: '100%'
                                                    }}
                                                />
                                            </div>

                                            <div className="form-field">
                                                <label className="form-label">Días de Crédito *</label>
                                                <select
                                                    value={formData.dias_credito}
                                                    onChange={(e) => handleInputChange('dias_credito', e.target.value)}
                                                    className="select-compact"
                                                    disabled={isLoading}
                                                    style={{
                                                        padding: '8px 12px',
                                                        border: '1px solid #d1d5db',
                                                        borderRadius: '6px',
                                                        fontSize: '14px',
                                                        width: '100%'
                                                    }}
                                                >
                                                    <option value="15">15 días</option>
                                                    <option value="30">30 días</option>
                                                    <option value="45">45 días</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {/* Tab: Direcciones */}
                    {activeTab === 'direcciones' && (
                        <div className="form-section-compact">
                            <div className="section-header-compact">
                                <label className="section-label-compact">
                                    <MapPin size={14} />
                                    Direcciones
                                </label>
                                {formData.direcciones.length < 3 && (
                                    <button
                                        type="button"
                                        className="add-btn-compact"
                                        onClick={agregarDireccion}
                                        disabled={isLoading}
                                        title="Agregar dirección"
                                    >
                                        <Plus size={14} />
                                    </button>
                                )}
                            </div>

                            <div className="direcciones-table">
                                <div className="direcciones-table-header">
                                    <div className="direcciones-col-direccion">Dirección</div>
                                    <div className="direcciones-col-principal">Predeterminada</div>
                                </div>
                                {formData.direcciones.map((direccion, index) => (
                                    <div
                                        key={index}
                                        className={`direcciones-table-row ${selectedDireccionIndex === index ? 'active-direction' : ''}`}
                                    >
                                        <div className="direcciones-col-direccion">
                                            <AddressInput
                                                value={direccion.direccion}
                                                onChange={(e) => handleDireccionChange(index, 'direccion', e.target.value)}
                                                onPlaceSelect={(placeData) => handleAddressAutocomplete(index, placeData)}
                                                onFocus={() => handleDireccionFocus(index)}
                                                placeholder="Escribe o busca una dirección, o selecciona en el mapa →"
                                                maxLength={255}
                                                disabled={isLoading}
                                            />

                                        </div>
                                        <div className="direcciones-col-principal">
                                            <label className="checkbox-compact-centered">
                                                <input
                                                    type="checkbox"
                                                    checked={direccion.es_principal}
                                                    onChange={(e) => handleDireccionChange(index, 'es_principal', e.target.checked)}
                                                    disabled={isLoading}
                                                />
                                            </label>
                                            {formData.direcciones.length > 1 && (
                                                <button
                                                    type="button"
                                                    className="delete-btn-compact"
                                                    onClick={() => eliminarDireccion(index)}
                                                    title="Eliminar"
                                                >
                                                    <Trash2 size={12} />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Tab: Datos Fiscales */}
                    {activeTab === 'fiscal' && (
                        <div className="form-section-compact">
                            <div className="section-header-compact">
                                <label className="section-label-compact">
                                    <FileText size={14} />
                                    Razones Sociales para Facturación
                                </label>
                                {formData.datos_fiscales.length < 5 && (
                                    <button
                                        type="button"
                                        className="add-btn-compact"
                                        onClick={agregarDatoFiscal}
                                        disabled={isLoading || !catalogosLoaded}
                                        title="Agregar razón social"
                                    >
                                        <Plus size={14} />
                                    </button>
                                )}
                            </div>

                            {!catalogosLoaded && (
                                <div className="loading-catalogos" style={{ padding: '1rem', textAlign: 'center', color: '#6b7280' }}>
                                    Cargando catálogos SAT...
                                </div>
                            )}

                            {catalogosLoaded && formData.datos_fiscales.length === 0 && (
                                <div className="empty-fiscal" style={{
                                    padding: '2rem',
                                    textAlign: 'center',
                                    background: '#f9fafb',
                                    borderRadius: '0.5rem',
                                    border: '1px dashed #d1d5db'
                                }}>
                                    <FileText size={32} style={{ color: '#9ca3af', marginBottom: '0.5rem' }} />
                                    <p style={{ margin: '0 0 0.5rem', color: '#6b7280' }}>
                                        No hay datos fiscales registrados
                                    </p>
                                    <button
                                        type="button"
                                        onClick={agregarDatoFiscal}
                                        disabled={isLoading}
                                        style={{
                                            padding: '0.5rem 1rem',
                                            background: '#667eea',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '0.375rem',
                                            cursor: 'pointer',
                                            fontSize: '0.875rem'
                                        }}
                                    >
                                        <Plus size={14} style={{ marginRight: '0.25rem', verticalAlign: 'middle' }} />
                                        Agregar Razón Social
                                    </button>
                                </div>
                            )}

                            {catalogosLoaded && formData.datos_fiscales.length > 0 && (
                                <div className="datos-fiscales-list">
                                    {formData.datos_fiscales.map((datoFiscal, index) => (
                                        <div key={index} className="dato-fiscal-card" style={{
                                            padding: '1rem',
                                            background: datoFiscal.es_principal ? '#eef2ff' : '#f9fafb',
                                            borderRadius: '0.5rem',
                                            border: datoFiscal.es_principal ? '2px solid #667eea' : '1px solid #e5e7eb',
                                            marginBottom: '1rem'
                                        }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                    <span style={{ fontWeight: 600, color: '#374151' }}>
                                                        Razón Social {index + 1}
                                                    </span>
                                                    {datoFiscal.es_principal && (
                                                        <span style={{
                                                            padding: '0.125rem 0.5rem',
                                                            background: '#667eea',
                                                            color: 'white',
                                                            borderRadius: '0.25rem',
                                                            fontSize: '0.75rem'
                                                        }}>
                                                            Principal
                                                        </span>
                                                    )}
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                    {!datoFiscal.es_principal && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleDatoFiscalChange(index, 'es_principal', true)}
                                                            style={{
                                                                padding: '0.25rem 0.5rem',
                                                                background: 'white',
                                                                border: '1px solid #d1d5db',
                                                                borderRadius: '0.25rem',
                                                                fontSize: '0.75rem',
                                                                cursor: 'pointer'
                                                            }}
                                                        >
                                                            Hacer principal
                                                        </button>
                                                    )}
                                                    <button
                                                        type="button"
                                                        className="delete-btn-compact"
                                                        onClick={() => eliminarDatoFiscal(index)}
                                                        title="Eliminar"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            </div>

                                            {/* RFC y C.P. Fiscal */}
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                                                <div className="form-field">
                                                    <label className="form-label" style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                                        RFC *
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={datoFiscal.rfc}
                                                        onChange={(e) => handleDatoFiscalChange(index, 'rfc', e.target.value)}
                                                        placeholder="XAXX010101000"
                                                        maxLength={13}
                                                        disabled={isLoading}
                                                        className={errors[`dato_fiscal_${index}_rfc`] ? 'input-error' : ''}
                                                        style={{
                                                            padding: '0.5rem',
                                                            border: errors[`dato_fiscal_${index}_rfc`] ? '1px solid #ef4444' : '1px solid #d1d5db',
                                                            borderRadius: '0.375rem',
                                                            fontSize: '0.875rem',
                                                            width: '100%',
                                                            textTransform: 'uppercase'
                                                        }}
                                                    />
                                                    {errors[`dato_fiscal_${index}_rfc`] && (
                                                        <span style={{ fontSize: '0.75rem', color: '#ef4444' }}>
                                                            {errors[`dato_fiscal_${index}_rfc`]}
                                                        </span>
                                                    )}
                                                    {datoFiscal.rfc && getTipoPersonaByRFC(datoFiscal.rfc) && (
                                                        <span style={{ fontSize: '0.7rem', color: '#059669' }}>
                                                            Persona {getTipoPersonaByRFC(datoFiscal.rfc) === 'fisica' ? 'Física' : 'Moral'}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="form-field">
                                                    <label className="form-label" style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                                        C.P. Fiscal *
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={datoFiscal.codigo_postal_fiscal}
                                                        onChange={(e) => handleDatoFiscalChange(index, 'codigo_postal_fiscal', e.target.value)}
                                                        placeholder="12345"
                                                        maxLength={5}
                                                        disabled={isLoading}
                                                        className={errors[`dato_fiscal_${index}_codigo_postal_fiscal`] ? 'input-error' : ''}
                                                        style={{
                                                            padding: '0.5rem',
                                                            border: errors[`dato_fiscal_${index}_codigo_postal_fiscal`] ? '1px solid #ef4444' : '1px solid #d1d5db',
                                                            borderRadius: '0.375rem',
                                                            fontSize: '0.875rem',
                                                            width: '100%'
                                                        }}
                                                    />
                                                    {errors[`dato_fiscal_${index}_codigo_postal_fiscal`] && (
                                                        <span style={{ fontSize: '0.75rem', color: '#ef4444' }}>
                                                            {errors[`dato_fiscal_${index}_codigo_postal_fiscal`]}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Razón Social */}
                                            <div className="form-field" style={{ marginBottom: '0.75rem' }}>
                                                <label className="form-label" style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                                    Razón Social *
                                                </label>
                                                <input
                                                    type="text"
                                                    value={datoFiscal.razon_social}
                                                    onChange={(e) => handleDatoFiscalChange(index, 'razon_social', e.target.value)}
                                                    placeholder="Nombre o Razón Social completa"
                                                    disabled={isLoading}
                                                    className={errors[`dato_fiscal_${index}_razon_social`] ? 'input-error' : ''}
                                                    style={{
                                                        padding: '0.5rem',
                                                        border: errors[`dato_fiscal_${index}_razon_social`] ? '1px solid #ef4444' : '1px solid #d1d5db',
                                                        borderRadius: '0.375rem',
                                                        fontSize: '0.875rem',
                                                        width: '100%'
                                                    }}
                                                />
                                                {errors[`dato_fiscal_${index}_razon_social`] && (
                                                    <span style={{ fontSize: '0.75rem', color: '#ef4444' }}>
                                                        {errors[`dato_fiscal_${index}_razon_social`]}
                                                    </span>
                                                )}
                                            </div>

                                            {/* Régimen Fiscal y Uso CFDI */}
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                                                <div className="form-field">
                                                    <label className="form-label" style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                                        Régimen Fiscal *
                                                    </label>
                                                    <select
                                                        value={datoFiscal.regimen_fiscal}
                                                        onChange={(e) => handleDatoFiscalChange(index, 'regimen_fiscal', e.target.value)}
                                                        disabled={isLoading}
                                                        className={errors[`dato_fiscal_${index}_regimen_fiscal`] ? 'input-error' : ''}
                                                        style={{
                                                            padding: '0.5rem',
                                                            border: errors[`dato_fiscal_${index}_regimen_fiscal`] ? '1px solid #ef4444' : '1px solid #d1d5db',
                                                            borderRadius: '0.375rem',
                                                            fontSize: '0.875rem',
                                                            width: '100%'
                                                        }}
                                                    >
                                                        <option value="">Seleccionar...</option>
                                                        {getRegimenesFiltrados(datoFiscal.rfc).map(r => (
                                                            <option key={r.clave} value={r.clave}>
                                                                {r.clave} - {r.descripcion}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {errors[`dato_fiscal_${index}_regimen_fiscal`] && (
                                                        <span style={{ fontSize: '0.75rem', color: '#ef4444' }}>
                                                            {errors[`dato_fiscal_${index}_regimen_fiscal`]}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="form-field">
                                                    <label className="form-label" style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                                        Uso CFDI (por defecto)
                                                    </label>
                                                    <select
                                                        value={datoFiscal.uso_cfdi}
                                                        onChange={(e) => handleDatoFiscalChange(index, 'uso_cfdi', e.target.value)}
                                                        disabled={isLoading}
                                                        style={{
                                                            padding: '0.5rem',
                                                            border: '1px solid #d1d5db',
                                                            borderRadius: '0.375rem',
                                                            fontSize: '0.875rem',
                                                            width: '100%'
                                                        }}
                                                    >
                                                        <option value="">Seleccionar...</option>
                                                        {getUsosCFDIFiltrados(datoFiscal.rfc).map(u => (
                                                            <option key={u.clave} value={u.clave}>
                                                                {u.clave} - {u.descripcion}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>

                                            {/* Email de facturación */}
                                            <div className="form-field">
                                                <label className="form-label" style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                                    Email para Facturación
                                                </label>
                                                <input
                                                    type="email"
                                                    value={datoFiscal.email_facturacion}
                                                    onChange={(e) => handleDatoFiscalChange(index, 'email_facturacion', e.target.value)}
                                                    placeholder="facturacion@ejemplo.com"
                                                    disabled={isLoading}
                                                    className={errors[`dato_fiscal_${index}_email_facturacion`] ? 'input-error' : ''}
                                                    style={{
                                                        padding: '0.5rem',
                                                        border: errors[`dato_fiscal_${index}_email_facturacion`] ? '1px solid #ef4444' : '1px solid #d1d5db',
                                                        borderRadius: '0.375rem',
                                                        fontSize: '0.875rem',
                                                        width: '100%'
                                                    }}
                                                />
                                                {errors[`dato_fiscal_${index}_email_facturacion`] && (
                                                    <span style={{ fontSize: '0.75rem', color: '#ef4444' }}>
                                                        {errors[`dato_fiscal_${index}_email_facturacion`]}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Nota informativa */}
                            <div style={{
                                marginTop: '1rem',
                                padding: '0.75rem',
                                background: '#fef3c7',
                                borderRadius: '0.375rem',
                                border: '1px solid #fcd34d',
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '0.5rem'
                            }}>
                                <AlertCircle size={16} style={{ color: '#92400e', flexShrink: 0, marginTop: '2px' }} />
                                <span style={{ fontSize: '0.8125rem', color: '#78350f' }}>
                                    Un cliente puede tener varias razones sociales para facturar. La marcada como "Principal" se usará por defecto al generar facturas.
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Botones de acción compactos */}
                <div className="form-actions-compact">
                    <Button
                        type="button"
                        variant="secondary"
                        size="small"
                        onClick={onCancel}
                        disabled={isLoading}
                    >
                        <X size={16} />
                        Cancelar
                    </Button>

                    <Button
                        type="submit"
                        size="small"
                        disabled={isLoading}
                    >
                        <Save size={16} />
                        {isEdit ? 'Actualizar' : 'Crear'}
                    </Button>
                </div>

            </form>

            {/* Mapa en vivo */}
            <div className="customer-form-map">
                <MapViewer
                    direcciones={formData.direcciones}
                    selectedDireccionIndex={selectedDireccionIndex}
                    onLocationSelect={handleLocationSelect}
                />
            </div>
        </div>
    );
};

export default CustomerForm;